import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { PageLoadingSpinner } from '~/components/ui/spinners/PageLoadingSpinner';
import { Input, Select } from '~/components/ui';
import { CurrencySelector } from '~/components/ui/inputs/CurrencySelector';
import { ProductLengthPicker } from '~/components/common/ProductLengthPicker';
import { useLengthPickerLabels } from '~/components/common/useLengthPickerLabels';
import {
  MagicLinkStatusCard,
  TokenErrorView,
} from '~/components/magic-link/MagicLinkStatusCard';
import { useTokenLoadedOnce } from '~/hooks/useTokenLoadedOnce';
import {
  getByToken,
  postByToken,
  tokenErrorCodeOf,
  type TokenErrorCode,
} from '~/lib/magicLinkToken';
import { MIN_POSITIVE_PRICE, MAX_PRICE } from '~/utils/moneyBounds';
import { quantityInBundles } from '~/pages/rfq-view/bundleQuantity';
import { priceUnitLabel } from '~/utils';
import {
  shipFromFacilityChoices,
  shipFromSelectOptions,
  type ShipFromFacility,
} from '~/utils/facilityOptions';
import { type Currency } from '~/graphql/graphql';

type QuoteItemLine = {
  quoteItemId: string;
  platformProductLengthId: string;
  productName: string;
  productLength: string;
  standardUnitsCode: string;
  quantityInPieces: number;
  piecesPerBundle: number;
  offeredPickupPrice: number | null;
  offeredDeliveryPrice: number | null;
};

type AvailableLengthLine = {
  lengthId: string;
  platformProductLengthId: string | null;
  productLength: string;
};

type RequestForQuoteItemLine = {
  productName: string;
  productLength: string;
  standardUnitsCode: string;
  quantityInPieces: number;
  piecesPerBundle: number;
  pickupPrice: number | null;
  deliveryPrice: number | null;
};

type RequestForQuoteSummary = {
  currency: Currency;
  leadTimeDays: number | null;
  pickupPriceForTally: number | null;
  deliveryPriceForTally: number | null;
  cannedTallyName?: string | null;
  cannedTallyCount?: number | null;
  items: RequestForQuoteItemLine[];
};

type QuoteData = {
  id: string;
  buyerOrganization: string;
  buyerFullName: string;
  sellerOrganization: string;
  useTallyPrice: boolean;
  items: QuoteItemLine[];
  availableLengths: AvailableLengthLine[];
  currency: Currency;
  pickupPriceForTally: number | null;
  deliveryPriceForTally: number | null;
  leadTimeDays: number | null;
  sellingOrganizationFacilityId: string | null;
  availableFacilities: ShipFromFacility[];
  requestForQuote: RequestForQuoteSummary;
};

function hasCannedTally(requestForQuote: RequestForQuoteSummary): boolean {
  return requestForQuote.cannedTallyName != null;
}

function fetchQuote(token: string): Promise<QuoteData> {
  return getByToken<QuoteData>(
    `/api/quotes/by-token?token=${encodeURIComponent(token)}`
  );
}

export function RespondToQuote() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const load = useTokenLoadedOnce(token, fetchQuote);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<TokenErrorCode | null>(null);
  const { t } = useTranslation('respond-to-quote');

  if (!token) {
    return (
      <TokenErrorView
        code="invalid"
        namespace="respond-to-quote"
        keyPrefix="respond_to_quote"
        testId="respond-quote-error"
      />
    );
  }

  if (load.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <PageLoadingSpinner name={t('respond_to_quote.loading')} />
      </div>
    );
  }

  if (load.status === 'error' || submitError) {
    const code =
      submitError ?? (load.status === 'error' ? load.code : 'unknown');
    return (
      <TokenErrorView
        code={code}
        namespace="respond-to-quote"
        keyPrefix="respond_to_quote"
        testId="respond-quote-error"
      />
    );
  }

  if (submitted) {
    return (
      <MagicLinkStatusCard
        testId="respond-quote-success"
        icon={<CheckCircle className="text-green-600" size={48} />}
        title={t('respond_to_quote.success.title')}
        message={t('respond_to_quote.success.message')}
      />
    );
  }

  return (
    <ResponseForm
      data={load.data}
      token={token}
      onSuccess={() => setSubmitted(true)}
      onError={setSubmitError}
    />
  );
}

type ItemPriceEdit = {
  pickupPrice: string;
  deliveryPrice: string;
  quantityInBundles: string;
};

type AddedLengthEdit = {
  lengthId: string;
  platformProductLengthId: string | null;
  productLength: string;
  quantityInBundles: string;
  pickupPrice: string;
  deliveryPrice: string;
};

function ResponseForm({
  data,
  token,
  onSuccess,
  onError,
}: {
  data: QuoteData;
  token: string;
  onSuccess: () => void;
  onError: (code: TokenErrorCode) => void;
}) {
  const { t } = useTranslation('respond-to-quote');
  const addLengthLabels = useLengthPickerLabels(
    t('respond_to_quote.form.add_length'),
    t('respond_to_quote.form.select_length_placeholder')
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pickupPrice, setPickupPrice] = useState(
    data.pickupPriceForTally != null ? String(data.pickupPriceForTally) : ''
  );
  const [deliveryPrice, setDeliveryPrice] = useState(
    data.deliveryPriceForTally != null ? String(data.deliveryPriceForTally) : ''
  );
  const [leadTimeDays, setLeadTimeDays] = useState(
    data.leadTimeDays != null ? String(data.leadTimeDays) : ''
  );
  const [currency, setCurrency] = useState<Currency>(data.currency);
  const [sellingOrganizationFacilityId, setSellingOrganizationFacilityId] =
    useState(
      data.sellingOrganizationFacilityId ??
        (data.availableFacilities.length === 1
          ? data.availableFacilities[0].id
          : '')
    );
  const [showFacilityError, setShowFacilityError] = useState(false);
  const [itemPrices, setItemPrices] = useState<Record<string, ItemPriceEdit>>(
    () =>
      Object.fromEntries(
        data.items.map((item) => [
          item.quoteItemId,
          {
            pickupPrice:
              item.offeredPickupPrice != null
                ? String(item.offeredPickupPrice)
                : '',
            deliveryPrice:
              item.offeredDeliveryPrice != null
                ? String(item.offeredDeliveryPrice)
                : '',
            quantityInBundles: String(
              quantityInBundles(item.quantityInPieces, item.piecesPerBundle)
            ),
          },
        ])
      )
  );
  const [addedLengths, setAddedLengths] = useState<AddedLengthEdit[]>([]);
  const [removedItemIds, setRemovedItemIds] = useState<Set<string>>(new Set());
  const submittedRef = useRef(false);

  const updateItemPrice = (
    quoteItemId: string,
    patch: Partial<ItemPriceEdit>
  ) =>
    setItemPrices((prev) => ({
      ...prev,
      [quoteItemId]: { ...prev[quoteItemId], ...patch },
    }));

  const addLength = (length: AvailableLengthLine) =>
    setAddedLengths((prev) => [
      ...prev,
      {
        lengthId: length.lengthId,
        platformProductLengthId: length.platformProductLengthId,
        productLength: length.productLength,
        quantityInBundles: '',
        pickupPrice: '',
        deliveryPrice: '',
      },
    ]);

  const updateAddedLength = (
    lengthId: string,
    patch: Partial<AddedLengthEdit>
  ) =>
    setAddedLengths((prev) =>
      prev.map((line) =>
        line.lengthId === lengthId ? { ...line, ...patch } : line
      )
    );

  const removeAddedLength = (lengthId: string) =>
    setAddedLengths((prev) =>
      prev.filter((line) => line.lengthId !== lengthId)
    );

  const hasFixedLines = hasCannedTally(data.requestForQuote);

  const removeItem = (quoteItemId: string) =>
    setRemovedItemIds((prev) => new Set(prev).add(quoteItemId));

  const removedItemLengths: AvailableLengthLine[] = data.items
    .filter((item) => removedItemIds.has(item.quoteItemId))
    .map((item) => ({
      lengthId: item.platformProductLengthId,
      platformProductLengthId: item.platformProductLengthId,
      productLength: item.productLength,
    }));

  const lengthsAvailableToAdd = [
    ...data.availableLengths,
    ...removedItemLengths,
  ].filter(
    (length) => !addedLengths.some((line) => line.lengthId === length.lengthId)
  );

  const addedLengthsAreValid = addedLengths.every((line) => {
    const hasValidQuantity =
      line.quantityInBundles.trim() !== '' &&
      parseInt(line.quantityInBundles, 10) > 0;
    if (data.useTallyPrice) {
      return hasValidQuantity;
    }
    return (
      hasValidQuantity &&
      (line.pickupPrice.trim() !== '' || line.deliveryPrice.trim() !== '')
    );
  });

  const activeItems = data.items.filter(
    (item) => !removedItemIds.has(item.quoteItemId)
  );

  const hasValidQuantities = activeItems.every((item) => {
    const quantity = itemPrices[item.quoteItemId].quantityInBundles;
    return quantity.trim() !== '' && parseInt(quantity, 10) > 0;
  });

  const hasAtLeastOneLine = activeItems.length > 0 || addedLengths.length > 0;

  const isFullyPriced =
    hasAtLeastOneLine &&
    (data.useTallyPrice
      ? (pickupPrice.trim() !== '' || deliveryPrice.trim() !== '') &&
        addedLengthsAreValid
      : activeItems.every((item) => {
          const prices = itemPrices[item.quoteItemId];
          return (
            prices.pickupPrice.trim() !== '' ||
            prices.deliveryPrice.trim() !== ''
          );
        }) && addedLengthsAreValid);

  const leadTimeDaysNum = leadTimeDays.trim()
    ? parseInt(leadTimeDays, 10)
    : NaN;
  const hasValidLeadTime =
    Number.isFinite(leadTimeDaysNum) && leadTimeDaysNum >= 0;

  const isMissingFacility =
    data.availableFacilities.length > 0 && !sellingOrganizationFacilityId;

  const handleSave = async (submit: boolean) => {
    if (isSubmitting || (submit && submittedRef.current)) {
      return;
    }
    if (!hasValidLeadTime) {
      return;
    }
    if (submit && (!isFullyPriced || !hasValidQuantities)) {
      return;
    }
    if (submit && isMissingFacility) {
      setShowFacilityError(true);
      return;
    }
    if (submit) {
      submittedRef.current = true;
    }
    setIsSubmitting(true);
    try {
      const itemOverrides = [
        ...data.items.map((item) => {
          if (removedItemIds.has(item.quoteItemId)) {
            return {
              quoteItemId: item.quoteItemId,
              platformProductLengthId: item.platformProductLengthId,
              quantityInBundles: 0,
              piecesPerBundle: item.piecesPerBundle,
              offeredPickupPrice: null,
              offeredDeliveryPrice: null,
            };
          }
          const edit = itemPrices[item.quoteItemId];
          return {
            quoteItemId: item.quoteItemId,
            platformProductLengthId: item.platformProductLengthId,
            quantityInBundles: parseInt(edit.quantityInBundles, 10),
            piecesPerBundle: item.piecesPerBundle,
            offeredPickupPrice:
              !data.useTallyPrice && edit.pickupPrice.trim()
                ? parseFloat(edit.pickupPrice)
                : null,
            offeredDeliveryPrice:
              !data.useTallyPrice && edit.deliveryPrice.trim()
                ? parseFloat(edit.deliveryPrice)
                : null,
          };
        }),
        ...addedLengths.map((line) => ({
          quoteItemId: null,
          platformProductLengthId: line.platformProductLengthId,
          lengthId: line.platformProductLengthId ? null : line.lengthId,
          quantityInBundles: parseInt(line.quantityInBundles, 10),
          piecesPerBundle: null,
          offeredPickupPrice:
            !data.useTallyPrice && line.pickupPrice.trim()
              ? parseFloat(line.pickupPrice)
              : null,
          offeredDeliveryPrice:
            !data.useTallyPrice && line.deliveryPrice.trim()
              ? parseFloat(line.deliveryPrice)
              : null,
        })),
      ];

      await postByToken('/api/quotes/respond-by-token', {
        token,
        currency,
        leadTimeDays: leadTimeDaysNum,
        pickupPriceForTally:
          data.useTallyPrice && pickupPrice.trim()
            ? parseFloat(pickupPrice)
            : null,
        deliveryPriceForTally:
          data.useTallyPrice && deliveryPrice.trim()
            ? parseFloat(deliveryPrice)
            : null,
        itemOverrides,
        sellingOrganizationFacilityId: sellingOrganizationFacilityId || null,
        submit,
      });
      if (submit) {
        onSuccess();
      } else {
        setIsSubmitting(false);
        toast.success(t('respond_to_quote.form.saved'));
      }
    } catch (err) {
      submittedRef.current = false;
      setIsSubmitting(false);
      toast.error(t('respond_to_quote.form.submit_error'));
      const code = tokenErrorCodeOf(err);
      if (code !== 'unknown') {
        onError(code);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 w-full flex items-start justify-center">
      <div className="bg-white rounded-xl shadow-md border border-gray-200 max-w-2xl w-full p-8">
        <h1 className="text-xl font-bold text-gray-900 mb-1">
          {t('respond_to_quote.form.title')}
        </h1>
        <p className="text-sm text-gray-600 mb-6">
          {t('respond_to_quote.form.subtitle', {
            buyerFullName: data.buyerFullName,
            buyerOrganization: data.buyerOrganization,
          })}
        </p>

        <RequestForQuoteSummarySection requestForQuote={data.requestForQuote} />

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave(true);
          }}
          className="flex flex-col gap-4"
        >
          <div className="max-w-xs">
            <CurrencySelector
              name="currency"
              label={`${t('respond_to_quote.form.currency')} *`}
              value={currency}
              onChange={(_, value) => setCurrency(value as Currency)}
            />
          </div>

          {data.availableFacilities.length > 0 && (
            <div className="max-w-xs">
              <Select
                name="selling-organization-facility"
                label={t('respond_to_quote.form.ship_from_facility')}
                value={sellingOrganizationFacilityId}
                onChange={(_, value) => setSellingOrganizationFacilityId(value)}
                error={
                  showFacilityError && isMissingFacility
                    ? t('respond_to_quote.form.ship_from_required')
                    : undefined
                }
                options={shipFromSelectOptions(
                  shipFromFacilityChoices(data.availableFacilities),
                  t('respond_to_quote.form.ship_from_placeholder')
                )}
              />
            </div>
          )}

          {data.useTallyPrice && (
            <>
              <Input
                name="pickup-price-for-tally"
                type="number"
                min={MIN_POSITIVE_PRICE}
                max={MAX_PRICE}
                step="0.01"
                label={t('respond_to_quote.form.pickup_price')}
                value={pickupPrice}
                onChange={(_, value) => setPickupPrice(value)}
              />
              <Input
                name="delivery-price-for-tally"
                type="number"
                min={MIN_POSITIVE_PRICE}
                max={MAX_PRICE}
                step="0.01"
                label={t('respond_to_quote.form.delivery_price')}
                value={deliveryPrice}
                onChange={(_, value) => setDeliveryPrice(value)}
              />
              <p className="text-xs text-gray-500 -mt-2">
                {t('respond_to_quote.form.price_requirement_hint')}
              </p>
            </>
          )}

          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {data.useTallyPrice
                ? t('respond_to_quote.form.tally_price_products')
                : t('respond_to_quote.form.requested_products')}
            </div>
            {!data.useTallyPrice && (
              <p className="px-4 pt-2 text-xs text-gray-500">
                {t('respond_to_quote.form.price_requirement_hint')}
              </p>
            )}
            <ul className="divide-y divide-gray-100">
              {data.items
                .filter((item) => !removedItemIds.has(item.quoteItemId))
                .map((item) => (
                  <ItemRow
                    key={item.quoteItemId}
                    rowId={item.quoteItemId}
                    name={`${item.productName} — ${item.productLength}`}
                    isFixed={hasFixedLines}
                    quantityValue={
                      itemPrices[item.quoteItemId].quantityInBundles
                    }
                    onQuantityChange={(value) =>
                      updateItemPrice(item.quoteItemId, {
                        quantityInBundles: value,
                      })
                    }
                    onRemove={() => removeItem(item.quoteItemId)}
                    priceFields={
                      data.useTallyPrice
                        ? undefined
                        : {
                            pickupLabel: priceLabel(
                              t('respond_to_quote.form.pickup_price'),
                              item.standardUnitsCode
                            ),
                            pickupValue:
                              itemPrices[item.quoteItemId].pickupPrice,
                            onPickupChange: (value) =>
                              updateItemPrice(item.quoteItemId, {
                                pickupPrice: value,
                              }),
                            deliveryLabel: priceLabel(
                              t('respond_to_quote.form.delivery_price'),
                              item.standardUnitsCode
                            ),
                            deliveryValue:
                              itemPrices[item.quoteItemId].deliveryPrice,
                            onDeliveryChange: (value) =>
                              updateItemPrice(item.quoteItemId, {
                                deliveryPrice: value,
                              }),
                          }
                    }
                  />
                ))}
              {addedLengths.map((line) => (
                <ItemRow
                  key={line.lengthId}
                  rowId={line.lengthId}
                  name={line.productLength}
                  isFixed={hasFixedLines}
                  highlighted
                  quantityValue={line.quantityInBundles}
                  onQuantityChange={(value) =>
                    updateAddedLength(line.lengthId, {
                      quantityInBundles: value,
                    })
                  }
                  onRemove={() => removeAddedLength(line.lengthId)}
                  priceFields={
                    data.useTallyPrice
                      ? undefined
                      : {
                          pickupLabel: t('respond_to_quote.form.pickup_price'),
                          pickupValue: line.pickupPrice,
                          onPickupChange: (value) =>
                            updateAddedLength(line.lengthId, {
                              pickupPrice: value,
                            }),
                          deliveryLabel: t(
                            'respond_to_quote.form.delivery_price'
                          ),
                          deliveryValue: line.deliveryPrice,
                          onDeliveryChange: (value) =>
                            updateAddedLength(line.lengthId, {
                              deliveryPrice: value,
                            }),
                        }
                  }
                />
              ))}
            </ul>
            {!hasFixedLines && lengthsAvailableToAdd.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-100">
                <ProductLengthPicker
                  className="max-w-xs"
                  testId="respond-to-quote-add-length"
                  lengths={lengthsAvailableToAdd.map((line) => ({
                    id: line.lengthId,
                    line,
                    length: {
                      id: line.lengthId,
                      displayValue: line.productLength,
                    },
                  }))}
                  labels={addLengthLabels}
                  onSelect={(picked) => addLength(picked.line)}
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('respond_to_quote.form.lead_time_days')} *
            </label>
            <input
              type="number"
              min="0"
              step="1"
              required
              value={leadTimeDays}
              onChange={(e) => setLeadTimeDays(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>

          <div className="mt-2 flex gap-3">
            <button
              type="button"
              onClick={() => handleSave(false)}
              disabled={isSubmitting || !hasValidLeadTime}
              className="inline-flex justify-center items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 disabled:opacity-50"
            >
              {isSubmitting
                ? t('respond_to_quote.form.saving')
                : t('respond_to_quote.form.save')}
            </button>
            <button
              type="submit"
              disabled={
                isSubmitting ||
                !isFullyPriced ||
                !hasValidQuantities ||
                !hasValidLeadTime
              }
              className="inline-flex justify-center items-center rounded-md bg-amber-800 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {isSubmitting
                ? t('respond_to_quote.form.sending')
                : t('respond_to_quote.form.send')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RequestForQuoteSummarySection({
  requestForQuote,
}: {
  requestForQuote: RequestForQuoteSummary;
}) {
  const { t } = useTranslation('respond-to-quote');
  return (
    <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
        {t('respond_to_quote.rfq.title')}
      </div>
      <div className="px-4 py-3 flex gap-6 text-sm border-b border-gray-100">
        <div>
          <span className="text-gray-500">
            {t('respond_to_quote.rfq.currency')}:{' '}
          </span>
          <span className="text-gray-900 font-medium">
            {requestForQuote.currency}
          </span>
        </div>
        <div>
          <span className="text-gray-500">
            {t('respond_to_quote.rfq.lead_time_days')}:{' '}
          </span>
          <span className="text-gray-900 font-medium">
            {requestForQuote.leadTimeDays ?? '—'}
          </span>
        </div>
      </div>
      {hasCannedTally(requestForQuote) && (
        <div
          className="px-4 py-3 text-sm border-b border-neut-border"
          data-testid="respond-quote-canned-tally"
        >
          <span className="text-neut-sec">
            {t('respond_to_quote.rfq.canned_tally')}:{' '}
          </span>
          <span className="text-neut-prim font-medium">
            {t('respond_to_quote.rfq.canned_tally_name_count', {
              name: requestForQuote.cannedTallyName,
              tallyCount: requestForQuote.cannedTallyCount,
            })}
          </span>
        </div>
      )}
      {(requestForQuote.pickupPriceForTally != null ||
        requestForQuote.deliveryPriceForTally != null) && (
        <div className="px-4 py-3 flex gap-6 text-sm border-b border-gray-100">
          {requestForQuote.pickupPriceForTally != null && (
            <div>
              <span className="text-gray-500">
                {t('respond_to_quote.form.pickup_price')}:{' '}
              </span>
              <span className="text-gray-900 font-medium">
                {requestForQuote.pickupPriceForTally}
              </span>
            </div>
          )}
          {requestForQuote.deliveryPriceForTally != null && (
            <div>
              <span className="text-gray-500">
                {t('respond_to_quote.form.delivery_price')}:{' '}
              </span>
              <span className="text-gray-900 font-medium">
                {requestForQuote.deliveryPriceForTally}
              </span>
            </div>
          )}
        </div>
      )}
      <ul className="divide-y divide-gray-100">
        {requestForQuote.items.map((item, index) => (
          <li
            key={`${item.productName}-${item.productLength}-${index}`}
            className="px-4 py-3 flex justify-between text-sm"
          >
            <span className="text-gray-900 font-medium">
              {item.productName} — {item.productLength}
            </span>
            <div className="flex gap-4 text-gray-600">
              {item.pickupPrice != null && (
                <span>
                  {priceLabel(
                    t('respond_to_quote.form.pickup_price'),
                    item.standardUnitsCode
                  )}
                  : {item.pickupPrice}
                </span>
              )}
              {item.deliveryPrice != null && (
                <span>
                  {priceLabel(
                    t('respond_to_quote.form.delivery_price'),
                    item.standardUnitsCode
                  )}
                  : {item.deliveryPrice}
                </span>
              )}
              <span>
                {t('respond_to_quote.form.quantity_bundles', {
                  count: quantityInBundles(
                    item.quantityInPieces,
                    item.piecesPerBundle
                  ),
                })}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function priceLabel(label: string, standardUnitsCode: string): string {
  return `${label} (/${priceUnitLabel(standardUnitsCode)})`;
}

type ItemRowPriceFields = {
  pickupLabel: string;
  pickupValue: string;
  onPickupChange: (value: string) => void;
  deliveryLabel: string;
  deliveryValue: string;
  onDeliveryChange: (value: string) => void;
};

function ItemRow({
  rowId,
  name,
  isFixed,
  quantityValue,
  onQuantityChange,
  onRemove,
  priceFields,
  highlighted,
}: {
  rowId: string;
  name: string;
  isFixed: boolean;
  quantityValue: string;
  onQuantityChange: (value: string) => void;
  onRemove: () => void;
  priceFields?: ItemRowPriceFields;
  highlighted?: boolean;
}) {
  const { t } = useTranslation('respond-to-quote');

  const quantityField = isFixed ? (
    <div className="w-24">
      <span className="block text-xs text-neut-sec mb-1">
        {t('respond_to_quote.form.bundles')}
      </span>
      <span
        data-testid={`${rowId}-fixed-quantity`}
        className="text-sm text-neut-prim tabular-nums"
      >
        {quantityValue}
      </span>
    </div>
  ) : (
    <div className="w-24">
      <label className="block text-xs text-gray-500 mb-1">
        {t('respond_to_quote.form.bundles')} *
      </label>
      <input
        type="number"
        min="0"
        step="1"
        value={quantityValue}
        onChange={(e) => onQuantityChange(e.target.value)}
        className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
      />
    </div>
  );

  const removeButton = isFixed ? null : (
    <button
      type="button"
      onClick={onRemove}
      className="text-xs text-gray-500 underline whitespace-nowrap pb-2"
    >
      {t('respond_to_quote.form.remove')}
    </button>
  );

  if (!priceFields) {
    return (
      <li
        className={`px-4 py-3 flex justify-between items-end gap-2 text-sm ${
          highlighted ? 'bg-amber-50/40' : ''
        }`}
      >
        <span className="text-gray-900 font-medium">{name}</span>
        <div className="flex items-end gap-2">
          {quantityField}
          {removeButton}
        </div>
      </li>
    );
  }

  return (
    <li
      className={`px-4 py-3 flex flex-col gap-2 ${
        highlighted ? 'bg-amber-50/40' : ''
      }`}
    >
      <div className="flex justify-between items-end gap-2 text-sm">
        <span className="text-gray-900 font-medium">{name}</span>
        {quantityField}
      </div>
      <div className="flex gap-2 items-end">
        <Input
          name={`${rowId}-pickup-price`}
          type="number"
          min={MIN_POSITIVE_PRICE}
          max={MAX_PRICE}
          step="0.01"
          label={priceFields.pickupLabel}
          value={priceFields.pickupValue}
          onChange={(_, value) => priceFields.onPickupChange(value)}
        />
        <Input
          name={`${rowId}-delivery-price`}
          type="number"
          min={MIN_POSITIVE_PRICE}
          max={MAX_PRICE}
          step="0.01"
          label={priceFields.deliveryLabel}
          value={priceFields.deliveryValue}
          onChange={(_, value) => priceFields.onDeliveryChange(value)}
        />
        {removeButton}
      </div>
    </li>
  );
}
